import { useCallback, useMemo, useState } from "react";
import { toast } from "sonner";
import { API_ENDPOINTS } from "../../../config/api-endpoints";
import { useServerPagination } from "../../../hooks/use-server-pagination";
import { useDelete, useGet, usePost } from "../../../packages/hooks";
import { buildApiUrl } from "../../../packages/hooks/hook-utils";
import { selectAuthToken, useAuthStore } from "../../../store/useAuth";
import { Brand } from "../../../types";
import { handleHttpError } from "../../../utils/error-utils";

/**
 * Custom hook for brand management using hooks from @hooks/ package
 * @param selectedBranchId - Optional selected branch ID for filtering
 * @returns Brand state and management functions
 */
export const useBrands = (selectedBranchId?: string | null) => {
  const [brands, setBrands] = useState<Brand[]>([]);
  const [totalItems, setTotalItems] = useState<number>(0);
  const [isUpdating, setIsUpdating] = useState<boolean>(false);

  // Get token from auth store
  const token = useAuthStore(selectAuthToken);

  // Initialize server-side pagination
  const pagination = useServerPagination(totalItems);

  // Memoize callbacks to prevent infinite loops
  const handleBrandsSuccess = useCallback((data: any) => {
    // Try multiple possible structures
    let brandList: Brand[] = [];
    let totalCount = 0;

    if (data) {
      // Check for brands array directly
      if (Array.isArray(data?.brands)) {
        brandList = data.brands;
        totalCount = data?.pagination?.total || brandList.length;
      }
      // Check for data.brands structure
      else if (Array.isArray(data?.data?.brands)) {
        brandList = data.data.brands;
        totalCount = data?.data?.pagination?.total || brandList.length;
      }
      // Check if data itself is an array
      else if (Array.isArray(data)) {
        brandList = data;
        totalCount = data.length;
      }
      // Check for data.data array structure
      else if (Array.isArray(data?.data)) {
        brandList = data.data;
        totalCount = data.data.length;
      }
    }

    setBrands(brandList);
    setTotalItems(totalCount);
  }, []);

  const handleBrandsError = useCallback((error: any) => {
    // Handle error silently in production
  }, []);

  // Build query params with optional branch filter
  const queryParams = useMemo(
    () => ({
      ...pagination.getQueryParams,
      ...(selectedBranchId && { branchId: selectedBranchId }),
    }),
    [pagination.getQueryParams, selectedBranchId]
  );

  // Initialize hooks
  const brandsQuery = useGet<{
    brands: Brand[];
    pagination: { total: number };
  }>(API_ENDPOINTS.BRANDS.LIST, {
    queryParams,
    onSuccess: handleBrandsSuccess,
    onError: handleBrandsError,
  });

  const createMutation = usePost<Brand>(API_ENDPOINTS.BRANDS.LIST, {
    onSuccess: () => {
      brandsQuery.refetch();
    },
  });

  const deleteMutation = useDelete<Brand>(API_ENDPOINTS.BRANDS.LIST, {
    onSuccess: () => {
      brandsQuery.refetch();
      toast.success("Бренд успешно удален");
    },
    onError: (error: any) => {
      // Show the actual error message from the backend
      const message = error?.message || "Произошла ошибка при удалении бренда";
      toast.error(message);
    },
  });

  const fetchBrands = useCallback(async (): Promise<void> => {
    await brandsQuery.refetch();
  }, [brandsQuery]);

  // Note: No manual refetch needed here - useGet automatically refetches when queryParams change
  // The queryKey in useGet includes queryParams, so React Query handles this automatically

  // TODO: Improve error handling and add proper response types
  const handleCreateBrand = async (values: Partial<Brand>): Promise<any> => {
    try {
      const response = await createMutation.mutateAsync({
        name: values.name || "",
        // Use branchId from values if provided, otherwise use selectedBranchId
        branchId: values.branchId || selectedBranchId || undefined,
      });
      return response;
    } catch (error) {
      return error;
    }
  };

  // TODO: Improve error handling and add proper response types

  const handleUpdateBrand = async (
    id: string,
    values: Partial<Brand>
  ): Promise<any> => {
    try {
      setIsUpdating(true);

      // Validate the ID
      if (!id || typeof id !== "string") {
        throw new Error("Invalid brand ID");
      }

      // Validate the token
      if (!token) {
        throw new Error("Authentication required");
      }

      // Construct endpoint with branchId query parameter
      let endpoint = `crm/brands/${id}`;
      if (selectedBranchId) {
        endpoint += `?branchId=${selectedBranchId}`;
      }
      const url = buildApiUrl(endpoint);

      if (!url) {
        throw new Error("URL is not available");
      }

      // Prepare request body
      const requestBody: any = {
        id: id,
        name: values.name || "",
      };

      // Add branchId if available
      const branchId = values.branchId || selectedBranchId;
      if (branchId) {
        requestBody.branchId = branchId;
      }

      const response = await fetch(url, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          "X-Admin-Authorization": `Bearer ${token}`,
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        await handleHttpError(response);
      }

      await brandsQuery.refetch();
      return response;
    } catch (error) {
      return error;
    } finally {
      setIsUpdating(false);
    }
  };

  const handleDeleteBrand = async (
    id: string,
    onSuccess?: () => void
  ): Promise<void> => {
    try {
      await deleteMutation.mutateAsync(id);
      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      // Error is already handled by the mutation's onError handler
      // Just prevent it from becoming an uncaught error
      console.log("Delete error caught:" + error);
    }
  };

  // Note: No initial fetch needed - useGet automatically fetches on mount

  return {
    brands,
    isLoading:
      brandsQuery.isLoading ||
      brandsQuery.isFetching ||
      createMutation.isPending ||
      isUpdating ||
      deleteMutation.isPending,
    totalItems,
    fetchBrands,
    handleCreateBrand,
    handleUpdateBrand,
    handleDeleteBrand,
    // Pagination handlers
    currentPage: pagination.currentPage,
    itemsPerPage: pagination.itemsPerPage,
    totalPages: pagination.totalPages,
    handlePageChange: pagination.handlePageChange,
    handleItemsPerPageChange: pagination.handleItemsPerPageChange,
  };
};
